import { get, post, put, del } from './api.service';
import { Trade, TradeCreateInput, TradeFilters, AddSaleInput } from '../types';
import { toTitleCase } from '../utils/formatters';

// Display-only: convert clientName (and any other name-ish fields) to Title Case.
// Backend payloads (create/update) are unaffected.
function titleCaseTrade<T extends Partial<Trade>>(t: T): T {
  return {
    ...t,
    clientName: t.clientName ? toTitleCase(t.clientName) : t.clientName,
  } as T;
}

interface TradesResponse {
  trades: Trade[];
}

interface TradeResponse {
  trade: Trade;
}

interface BulkImportResult {
  results: {
    success: number;
    failed: number;
    errors: { index: number; error: string }[];
  };
}

// ============================================================================
// TRADE SERVICE
// ============================================================================

export const tradeService = {
  /**
   * Get all trades with optional filters
   */
  async getTrades(filters?: TradeFilters): Promise<Trade[]> {
    const params: Record<string, string> = {};

    if (filters?.clientId) params.clientId = filters.clientId;
    if (filters?.status) params.status = filters.status;
    if (filters?.tradeType) params.tradeType = filters.tradeType;
    if (filters?.scrip) params.scrip = filters.scrip;
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;

    const response = await get<TradesResponse>('/trades', params);
    return (response.trades || []).map(titleCaseTrade);
  },

  /**
   * Get a specific trade by ID
   */
  async getTradeById(tradeId: string): Promise<Trade> {
    const response = await get<TradeResponse>(`/trades/${tradeId}`);
    return titleCaseTrade(response.trade);
  },

  /**
   * Create a new trade
   */
  async createTrade(data: TradeCreateInput): Promise<Trade> {
    const response = await post<TradeResponse>('/trades', data);
    return titleCaseTrade(response.trade);
  },

  /**
   * Update an existing trade
   */
  async updateTrade(tradeId: string, data: Partial<TradeCreateInput>): Promise<Trade> {
    const response = await put<TradeResponse>(`/trades/${tradeId}`, data);
    return titleCaseTrade(response.trade);
  },

  /**
   * Add sale to an existing open trade
   */
  async addSale(tradeId: string, data: AddSaleInput): Promise<Trade> {
    const response = await post<TradeResponse>(`/trades/${tradeId}/add-sale`, data);
    return titleCaseTrade(response.trade);
  },

  /**
   * Delete a trade (soft delete)
   */
  async deleteTrade(tradeId: string): Promise<void> {
    await del(`/trades/${tradeId}`);
  },

  /**
   * HARD-wipe ALL trades + import lineage (admin/super-admin only). Irreversible.
   */
  async deleteAllTrades(): Promise<{ trades: number; fills: number; batches: number; clientsReset: number }> {
    return post<{ trades: number; fills: number; batches: number; clientsReset: number }>('/trades/delete-all', {});
  },

  /**
   * LIFO sell from a merged position — distributes sell across legs (newest first)
   */
  async lifoSell(data: { clientId: string; scrip: string; sellShares: number; salePrice: number; saleDate?: string }): Promise<{ message: string; trades: Trade[] }> {
    const response = await post<{ message: string; trades: Trade[] }>('/trades/lifo-sell', data);
    return { ...response, trades: (response.trades || []).map(titleCaseTrade) };
  },

  /**
   * Bulk import trades
   */
  async bulkImport(trades: TradeCreateInput[]): Promise<BulkImportResult> {
    const response = await post<BulkImportResult>('/trades/bulk-import', { trades });
    return response;
  },

  /**
   * Get trades by client
   */
  async getTradesByClient(clientId: string, filters?: Partial<TradeFilters>): Promise<Trade[]> {
    const params: Record<string, string> = {};

    if (filters?.status) params.status = filters.status;
    if (filters?.tradeType) params.tradeType = filters.tradeType;
    if (filters?.scrip) params.scrip = filters.scrip;
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;

    const response = await get<TradesResponse>(`/clients/${clientId}/trades`, params);
    return (response.trades || []).map(titleCaseTrade);
  },
};

export default tradeService;
